import React, { PureComponent } from "react";
import styled from "styled-components";
import { putRequest } from "../../../api/ApiService";
import { incrementUnacceptedDecisions } from "../../../api/SubmitDecisions";
import { logError } from "../../../services/utils";
import Auth from "../../../routes/Auth";
import Teams from "../../../api/Teams";
import ErrorAlert from "../../alerts/error";
import SuccessAlert from "../../alerts/success";
import FinanceStyled from "../../finance/style";

// Trailing slash matters: without it Django redirects 301 → /bulk/ and
// the browser drops the PUT body across the redirect.
const SALES_BULK_URL = `${process.env.REACT_APP_API_URL}team/sales/bulk/`;

const FONT = "'Mukta Vaani', Lato, sans-serif";

// Friendly description per channel name. Falls back to the channel
// name if no entry exists. Keys match the renamed labels emitted by
// PMDecisions ("SalesRep" → "Direct Sales", "Distributor1" → "Distributors").
const CHANNEL_META = {
  "Direct Sales": {
    description: "Your in-house team sells the product straight to customers.",
  },
  "Distributors": {
    description: "Third-party distributors carry the product into wider channels.",
  },
};

// ---------- styled components ----------

const Page = styled.div`
  padding: 24px 40px 64px;
  max-width: 1320px;
  margin: 0 auto;
  font-family: ${FONT};
`;

const ToastSlot = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  justify-content: center;
  pointer-events: none;
  margin-bottom: -12px;

  & > * {
    pointer-events: auto;
  }
`;

const HeaderBlock = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const TitleIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #DBEAFE;
  color: #2563EB;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: rgba(0, 27, 59, 0.6);
  line-height: 1.5;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  padding: 4px 10px;
  line-height: 1;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
  color: rgba(0, 27, 59, 0.94);
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const BtnSecondary = styled.button`
  background: transparent;
  border: 1.5px solid #E5E9F0;
  color: rgba(0, 27, 59, 0.6);
  padding: 12px 22px;
  border-radius: 12px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: rgba(0, 27, 59, 0.6);
    color: rgba(0, 27, 59, 0.94);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BtnPrimary = styled.button`
  background: #3A507B;
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(58, 80, 123, 0.25);

  &:hover:not(:disabled) {
    background: #2E3F62;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

// Card lights up navy when at least one channel is checked, matching
// the affordance used on the other decision pages.
const ProductCard = styled.div`
  border: 1.5px solid ${p => (p.selected ? "#3A507B" : "#E5E9F0")};
  background: #FFFFFF;
  border-radius: 14px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${p => (p.selected
    ? "0 6px 20px rgba(58, 80, 123, 0.18)"
    : "none")};

  &:hover {
    box-shadow: ${p => (p.selected
      ? "0 8px 24px rgba(58, 80, 123, 0.24)"
      : "0 4px 16px rgba(15, 23, 41, 0.06)")};
  }
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProductIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #F5F7FB;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 27, 59, 0.6);
  flex-shrink: 0;
`;

const ProductName = styled.div`
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  color: rgba(0, 27, 59, 0.94);
  word-break: break-word;
`;

const ChannelLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const ChannelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Each channel row is a clickable label with a custom checkbox affordance
// on the left, the channel name in the middle, and a short description
// below. Selecting highlights the row navy.
const ChannelOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: ${p => (p.checked ? "#F5F8FF" : "#F5F7FB")};
  border: 1.5px solid ${p => (p.checked ? "#3A507B" : "transparent")};
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${p => (p.checked ? "#EFF3FF" : "#EEF1F5")};
  }
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

// Square checkbox with a navy check when ticked. Avoids the browser's
// default checkbox so the design is consistent across platforms.
const CheckboxVisual = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid ${p => (p.checked ? "#3A507B" : "rgba(0, 27, 59, 0.25)")};
  background: ${p => (p.checked ? "#3A507B" : "#FFFFFF")};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  transition: border-color 0.15s ease, background 0.15s ease;

  &::after {
    content: '';
    width: 10px;
    height: 6px;
    border-left: 2px solid #FFFFFF;
    border-bottom: 2px solid #FFFFFF;
    transform: rotate(-45deg) translate(1px, -1px);
    display: ${p => (p.checked ? "block" : "none")};
  }
`;

const ChannelContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ChannelName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.94);
`;

const ChannelDesc = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 2px;
`;

const EmptyCard = styled.div`
  border: 1.5px solid #E5E9F0;
  background: #FFFFFF;
  border-radius: 14px;
  padding: 22px;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.6);
`;

// ---------- no-sales-channel confirmation modal ----------
//
// Fires when the user tries to Select while at least one released
// product still has no sales channel ticked. Two buttons: Cancel
// returns them to the checkboxes; Continue commits the save with the
// un-channel'd products intact.

const ConfirmBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  z-index: 1200;
`;

const ConfirmCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 460px;
  padding: 28px 28px 24px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  font-family: 'Mukta Vaani', Lato, sans-serif;
  color: rgba(0, 27, 59, 0.94);
`;

const ConfirmTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 800;
`;

const ConfirmBody = styled.div`
  font-size: 16px;
  line-height: 1.55;
  margin-bottom: 20px;
`;

const ConfirmProductList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  font-weight: 700;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ConfirmSecondaryBtn = styled.button`
  padding: 10px 20px;
  border: 1.5px solid #D5D9DD;
  background: #ffffff;
  border-radius: 8px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;
`;

const ConfirmPrimaryBtn = styled.button`
  padding: 10px 20px;
  border: 0;
  background: #2563EB;
  border-radius: 8px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: #ffffff;
  cursor: pointer;
`;

// ---------- component ----------

class ManagePlacementPage extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      // Selection map keyed by product id; each inner Set holds the
      // sales-client ids selected for that product. Switching products
      // doesn't apply here (all cards visible at once) — the map just
      // tracks which boxes are ticked.
      selections: {},
      isSaving: false,
      errorMessage: null,
      successMessage: null,
      // Names of released products with no sales channel selected.
      // When non-null, the "continue anyway?" confirmation modal
      // renders and the actual save is deferred until the user picks
      // a button.
      noChannelProductNames: null,
    };
    this._successTimer = null;
  }

  componentDidMount() {
    this._mounted = true;
    this.initSelections(this.props);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.sales !== this.props.sales
        || nextProps.products !== this.props.products
        || nextProps.sales_clients !== this.props.sales_clients) {
      this.initSelections(nextProps);
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._successTimer) {
      clearTimeout(this._successTimer);
      this._successTimer = null;
    }
  }

  // Seed the selection map from the existing sales rows. Each (product,
  // client) pair where player !== null marks that channel as ticked
  // for that product.
  initSelections = (props) => {
    const selections = {};
    (props.products || []).forEach(product => {
      selections[product.id] = {};
    });
    (props.sales || []).forEach(row => {
      if (row.player === null) return;
      const productId = Number(row.product);
      const clientId = Number(row.client);
      if (selections[productId]) {
        selections[productId][clientId] = true;
      }
    });
    this.setState({ selections });
  };

  toggleSelection = (productId, clientId) => () => {
    if (!this.props.actionsAllowed) return;
    const { selections } = this.state;
    const productMap = { ...(selections[productId] || {}) };
    if (productMap[clientId]) {
      delete productMap[clientId];
    } else {
      productMap[clientId] = true;
    }
    this.setState({
      selections: {
        ...selections,
        [productId]: productMap,
      },
    });
  };

  // Resets ticks to whatever's currently saved on the server (not
  // a hard "everything off" wipe). Matches the convention used on the
  // promo and pricing pages.
  resetAll = () => {
    this.initSelections(this.props);
  };

  // ---------- save ----------

  hideErrorMessage = () => {
    this.setState({ errorMessage: null });
  };

  showLocalSuccess = (message) => {
    if (this._successTimer) clearTimeout(this._successTimer);
    this.setState({ successMessage: message });
    this._successTimer = setTimeout(() => {
      if (!this._mounted) return;
      this.setState({ successMessage: null });
    }, 5000);
  };

  resolveBulkContext = () => {
    const { rounds = [], currentRound } = this.props;
    const teamId = Auth.getTeamId();
    const player = Teams.currentPlayer();
    const playerId = player ? player.id : null;
    const round = rounds.find(r => Number(r.number) === Number(currentRound));
    const roundId = round ? round.id : null;
    return { teamId, playerId, roundId };
  };

  postSelections = () => {
    if (!this.props.actionsAllowed) return;
    const { products = [] } = this.props;

    if (products.length === 0) {
      this.setState({
        errorMessage: "There are no products to place. Coordinate with Product Development first.",
      });
      return;
    }

    // Soft-warn on released products with no sales channel selected
    // (empty inner selection map means no sales row will be posted for
    // that product, which is the same as "no sales.id" once saved).
    // Missing months_available_to_produce is treated as "released" per
    // the game rule so we don't quietly let an unchannel'd product
    // slip through when the field is absent.
    const { selections } = this.state;
    const noChannelReleasedNames = products.filter(product => {
      const productMap = selections[product.id] || {};
      const hasAnySelection = Object.keys(productMap).some(cid => productMap[cid]);
      if (hasAnySelection) return false;
      const months = product.months_available_to_produce != null
        ? Number(product.months_available_to_produce)
        : 1;
      return months > 0;
    }).map(p => p.name);
    if (noChannelReleasedNames.length > 0) {
      this.setState({ noChannelProductNames: noChannelReleasedNames });
      return;
    }

    this.doPostSelections();
  };

  confirmNoChannelPost = () => {
    this.setState({ noChannelProductNames: null });
    this.doPostSelections();
  };

  cancelNoChannelPost = () => {
    this.setState({ noChannelProductNames: null });
  };

  doPostSelections = () => {
    const { products = [] } = this.props;
    const { teamId, playerId, roundId } = this.resolveBulkContext();
    if (!teamId || !playerId || !roundId) {
      this.setState({
        errorMessage: "We couldn't determine your team, round, or player context. Please refresh the page and try again.",
      });
      return;
    }

    // Atomic-replace: send the complete sales matrix. Each ticked
    // (product, client) becomes an entry; unticked combinations are
    // omitted entirely (the backend wipes prior rows then inserts the
    // payload). Empty sales[] = clear all this round's sales decisions.
    const { selections } = this.state;
    const sales = [];
    products.forEach(product => {
      const productMap = selections[product.id] || {};
      Object.keys(productMap).forEach(clientId => {
        if (productMap[clientId]) {
          sales.push({
            product: product.id,
            client: Number(clientId),
          });
        }
      });
    });

    const body = {
      team: teamId,
      round: roundId,
      player: playerId,
      sales,
    };

    this.setState({ isSaving: true });

    putRequest(SALES_BULK_URL).send(body)
      .then(() => {
        // The atomic-replace endpoint can result in zero net new rows
        // (re-selecting an existing matrix), so only bump unaccepted if
        // anything was actually selected this round.
        if (sales.length > 0) {
          incrementUnacceptedDecisions("marketing");
        }
        if (this.props.reloadData) this.props.reloadData();
        if (this._mounted) {
          this.setState({ isSaving: false });
          this.showLocalSuccess("Sales channel selections were successfully saved.");
        }
      })
      .catch(err => {
        logError(err, "ManagePlacementPage", "postSelections");
        if (this._mounted) {
          this.setState({
            isSaving: false,
            errorMessage: "Something went wrong saving your placement selections. Please try again.",
          });
        }
      });
  };

  // ---------- render ----------

  renderProductIcon() {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        width="22" height="22">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    );
  }

  renderProductCard = (product) => {
    const { actionsAllowed, sales_clients = [] } = this.props;
    const productMap = this.state.selections[product.id] || {};
    const selectedCount = Object.keys(productMap).filter(k => productMap[k]).length;

    return (
      <ProductCard key={product.id} selected={selectedCount > 0}>
        <ProductHeader>
          <ProductIconWrap>{this.renderProductIcon()}</ProductIconWrap>
          <ProductName>{product.name}</ProductName>
        </ProductHeader>
        <div>
          <ChannelLabel>Sales Channels</ChannelLabel>
          <ChannelList>
            {sales_clients.length === 0 && (
              <ChannelDesc>No sales channels available.</ChannelDesc>
            )}
            {sales_clients.map(client => {
              const checked = !!productMap[client.id];
              const meta = CHANNEL_META[client.name] || {};
              return (
                <ChannelOption key={client.id} checked={checked}>
                  <HiddenCheckbox
                    type="checkbox"
                    checked={checked}
                    onChange={this.toggleSelection(product.id, client.id)}
                    disabled={!actionsAllowed}
                  />
                  <CheckboxVisual checked={checked} />
                  <ChannelContent>
                    <ChannelName>{client.name}</ChannelName>
                    {meta.description && (
                      <ChannelDesc>{meta.description}</ChannelDesc>
                    )}
                  </ChannelContent>
                </ChannelOption>
              );
            })}
          </ChannelList>
        </div>
      </ProductCard>
    );
  };

  render() {
    const { products = [], actionsAllowed, onClose } = this.props;
    const {
      isSaving, errorMessage, successMessage, noChannelProductNames,
    } = this.state;

    return (
      <FinanceStyled.LenderOverlay>
        {errorMessage && (
          <ErrorAlert
            title="Heads up"
            message={errorMessage}
            onHide={this.hideErrorMessage}
          />
        )}
        {noChannelProductNames && noChannelProductNames.length > 0 && (
          <ConfirmBackdrop role="dialog" aria-modal="true">
            <ConfirmCard>
              <ConfirmTitle>Continue with no sales channel?</ConfirmTitle>
              <ConfirmBody>
                {noChannelProductNames.length === 1
                  ? "This released product has no sales channel selected:"
                  : "These released products have no sales channel selected:"}
                <ConfirmProductList>
                  {noChannelProductNames.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ConfirmProductList>
                <div style={{ marginTop: 12 }}>
                  Without a sales channel, customers might find it difficult
                  to buy
                  {noChannelProductNames.length === 1 ? " this product." : " these products."}
                  {" "}Go back to pick a channel, or continue anyway.
                </div>
              </ConfirmBody>
              <ConfirmActions>
                <ConfirmSecondaryBtn onClick={this.cancelNoChannelPost}>
                  Go Back
                </ConfirmSecondaryBtn>
                <ConfirmPrimaryBtn onClick={this.confirmNoChannelPost}>
                  Continue
                </ConfirmPrimaryBtn>
              </ConfirmActions>
            </ConfirmCard>
          </ConfirmBackdrop>
        )}
        <Page>
          {successMessage && (
            <ToastSlot>
              <SuccessAlert message={successMessage} />
            </ToastSlot>
          )}
          <HeaderBlock>
            <HeaderLeft>
              <TitleRow>
                <TitleIcon>🛒</TitleIcon>
                <Title>Manage Placement</Title>
              </TitleRow>
              <Subtitle>
                Choose which sales channels carry each of your products. You can
                pick more than one channel per product.
              </Subtitle>
            </HeaderLeft>
            <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
          </HeaderBlock>

          <SectionHeader>
            <SectionTitle>Product Placement</SectionTitle>
            <FooterButtons>
              <BtnSecondary onClick={this.resetAll} disabled={!actionsAllowed || isSaving}>
                Reset All
              </BtnSecondary>
              <BtnPrimary
                onClick={this.postSelections}
                disabled={!actionsAllowed || isSaving}
              >
                <span>{isSaving ? "Saving…" : "Select"}</span>
                {!isSaving && <span>→</span>}
              </BtnPrimary>
            </FooterButtons>
          </SectionHeader>

          {products.length === 0 ? (
            <EmptyCard>
              No products available yet. Coordinate with your Head of Product
              (or CEO) to develop a product first.
            </EmptyCard>
          ) : (
            <ProductsGrid>
              {products.map(this.renderProductCard)}
            </ProductsGrid>
          )}
        </Page>
      </FinanceStyled.LenderOverlay>
    );
  }
}

export default ManagePlacementPage;



// WEBPACK FOOTER //
// ./src/components/pm/decisions/manage-placement-page.js